import {
  GroupedObjectInfo,
  ObjectCollection,
  ObjectQuery,
  RendererObservationEvent,
} from '@fullstory/renderer-shared';
import { isEqual } from 'lodash';

export type ObjectObserverCallback = (collection: ObjectCollection) => any;

/** Metadata about a group associated with some ObjectPattern. */
type ObjectPatternGroupMetadata = {
  /**
   * The observer associated with this group. RendererObjectObserverDispatcher
   * namespaces groups by observer, so there's always exactly one observer
   * associated with each group.
   */
  observer: ObjectObserverCallback;
  /**
   * The original group name from the ObjectPattern passed to addObserver(), if
   * any. RendererObjectObserverDispatcher sometimes creates groups internally
   * for bookkeeping purposes, so this is not always present.
   */
  observerGroup?: string;
};

/**
 * RendererObjectObserverDispatcher manages object observers, multiplexes all
 * of their individual queries into a single observeObjects() query, and
 * dispatches observations to the appropriate observers.
 */
export class RendererObjectObserverDispatcher {
  #nextObjectObserverId = 0;
  #objectObservers: Map<ObjectObserverCallback, ObjectQuery> = new Map();
  #objectGroups: Map<string, ObjectPatternGroupMetadata> = new Map();
  #lastObservation: ObjectCollection = {};

  addObserver = (observer: ObjectObserverCallback, observerQuery: ObjectQuery) => {
    const id = this.#nextObjectObserverId++;
    const namespacedQuery: ObjectQuery = { ...observerQuery, patterns: [] };

    for (const observerPattern of observerQuery.patterns) {
      const groups = [];

      // Tag every group with an observer-specific identifier, so that groups
      // are namespaced and don't conflict between observers.
      for (const observerGroup of observerPattern.groups ?? []) {
        const group = `${id}:${observerGroup}`;
        this.#objectGroups.set(group, { observer, observerGroup });
        groups.push(group);
      }

      // If no groups were specified, create a default group that will be used
      // only to identify the observer. This ensures that we can always tell
      // which observer is interested in a particular object just by looking at
      // the groups it was associated with.
      if (groups.length === 0) {
        const observerIdGroup = `${id}:`;
        this.#objectGroups.set(observerIdGroup, { observer });
        groups.push(observerIdGroup);
      }

      namespacedQuery.patterns.push({ ...observerPattern, groups });
    }

    this.#objectObservers.set(observer, namespacedQuery);
  };

  removeObserver = (observerToRemove: ObjectObserverCallback) => {
    this.#objectObservers.delete(observerToRemove);
    for (const [group, { observer }] of this.#objectGroups) {
      if (observer === observerToRemove) {
        this.#objectGroups.delete(group);
      }
    }
  };

  /** @returns An observeObjects() query that combines all observer queries. */
  query = (): ObjectQuery => {
    const mergedQuery: ObjectQuery = { patterns: [] };
    for (const observerQuery of this.#objectObservers.values()) {
      mergedQuery.patterns.push(...observerQuery.patterns);
      if (observerQuery.computeDescendants) {
        // if any observer requested descendants, we should request them for all
        // TODO: optimize if this becomes an issue for callers with mixed requirements
        mergedQuery.computeDescendants = true;
      }
    }
    return mergedQuery;
  };

  onObservation = (event: RendererObservationEvent) => {
    const affectedObservers: Set<ObjectObserverCallback> = new Set();

    // The renderer sends us a complete snapshot of the objects it sees on each
    // observation, even if only some of them have changed. As an optimization,
    // we compare the new observation to the previous one and only notify
    // observers if there's a change that affects them. The definition of
    // "affects them" is a bit nuanced, and we actually don't do a perfect job
    // here; group changes trigger notifications even to observers who can't see
    // those groups. This is a tradeoff to keep the implementation simple, since
    // we expect this to be rare in practice and unlikely to cause a performance
    // issue. If that assumption turns out to be wrong, we can revisit this.

    // Identify changes in objects that appear in the new observation.
    for (const [path, objectInfo] of Object.entries(event.objects)) {
      const lastObjectInfo = this.#lastObservation[path];
      if (isEqual(lastObjectInfo, objectInfo)) {
        continue;
      }
      for (const group of lastObjectInfo?.groups ?? []) {
        const groupInfo = this.#objectGroups.get(group);
        if (groupInfo) {
          affectedObservers.add(groupInfo.observer);
        }
      }
      for (const group of objectInfo.groups) {
        const groupInfo = this.#objectGroups.get(group);
        if (groupInfo) {
          affectedObservers.add(groupInfo.observer);
        }
      }
    }

    // Identify changes in objects that disappeared in the new observation.
    for (const [path, lastObjectInfo] of Object.entries(this.#lastObservation)) {
      for (const group of lastObjectInfo.groups) {
        if (event.objects[path]?.groups?.includes(group)) {
          continue;
        }
        const groupInfo = this.#objectGroups.get(group);
        if (groupInfo) {
          affectedObservers.add(groupInfo.observer);
        }
      }
    }

    // A helper that converts the ObjectInfo sent to us by the renderer into a
    // view of that ObjectInfo for a specific observer. Each observer sees the
    // same information about the object itself, but they only see their own
    // groups.
    const objectInfoForObserver = (
      observer: ObjectObserverCallback,
      objectInfo: GroupedObjectInfo,
    ): GroupedObjectInfo | undefined => {
      let matched = false;
      const observerGroups: string[] = [];
      for (const group of objectInfo.groups) {
        const groupInfo = this.#objectGroups.get(group);
        if (groupInfo?.observer !== observer) {
          continue;
        }
        if (groupInfo.observerGroup !== undefined) {
          observerGroups.push(groupInfo.observerGroup);
        }
        matched = true;
      }
      return matched ? { ...objectInfo, groups: observerGroups } : undefined;
    };

    // Dispatch observations to the affected observers.
    for (const observer of affectedObservers) {
      const collection: ObjectCollection = {};
      for (const [path, objectInfo] of Object.entries(event.objects)) {
        const observerObjectInfo = objectInfoForObserver(observer, objectInfo);
        if (!observerObjectInfo) {
          continue;
        }
        collection[path] = observerObjectInfo;
      }
      observer(collection);
    }

    this.#lastObservation = event.objects;
  };
}
